import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createPortalApi } from '@/services/api';
import type { AuthApi } from '@/services/api/auth-api';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';
import type { PortalClient } from '@/services/api/portal-client';
import type { Credentials, PortalUser } from '@/services/api/types';

import { AuthContext, type AuthContextValue, type AuthStatus } from '@/features/auth/auth-context';
import { clearSession, loadSession, saveCredentials, saveSession } from '@/features/auth/credential-storage';
import { restoreUserFromJwt } from '@/features/auth/jwt';

export interface AuthProviderProps {
  children: ReactNode;
  /**
   * Injected auth API. Provided by tests to mock the network boundary; in the
   * app it defaults to a real client wired to the Portal.
   */
  auth?: AuthApi;
  /** Registers a global "session invalidated" hook (real client only). */
  onRegisterUnauthorized?: (handler: () => void) => void;
}

/**
 * Owns authentication state and exposes it via {@link AuthContext}.
 *
 * Session restoration is verified against the real Portal API (not just local
 * state): on mount the stored credentials are re-validated with a login call, so
 * a revoked or expired account can never resurrect an authenticated shell.
 */
export function AuthProvider({ children, auth, onRegisterUnauthorized }: AuthProviderProps): ReactNode {
  // Build the real API once when no test double is injected.
  const api = useMemo(() => (auth ? buildInjectedApi(auth) : buildRealApi()), [auth]);
  const authApi = auth ?? api.auth;
  const client = api.client;

  const [status, setStatus] = useState<AuthStatus>(() =>
    // Read sessionStorage synchronously during render (not in an effect) to set
    // the correct initial status without triggering a cascading render.
    loadSession() !== null ? 'initializing' : 'unauthenticated',
  );
  const [user, setUser] = useState<PortalUser | null>(null);
  const [error, setError] = useState<ApiErrorKey | null>(null);
  const [busy, setBusy] = useState(false);
  // Set right after organization creation so the app immediately treats the session as "has
  // organization" without forcing a full session reload. Reset on sign-out.
  const [organizationJustCreated, setOrganizationJustCreated] = useState(false);

  const credentialsRef = useRef<Credentials | null>(null);
  const restoreStartedRef = useRef(false);
  const mountedRef = useRef(true);

  const applySignedOut = useCallback(() => {
    credentialsRef.current = null;
    clearSession();
    client?.setCredentials(null);
    client?.setJwt(null);
    setUser(null);
    setOrganizationJustCreated(false);
    setStatus('unauthenticated');
  }, [client]);

  // Register the global unauthorized handler (expired/invalid session).
  useEffect(() => {
    const register = onRegisterUnauthorized ?? api.register;
    register?.(() => {
      setError('sessionExpired');
      applySignedOut();
    });
  }, [api, onRegisterUnauthorized, applySignedOut]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Restore & verify a stored session on startup.
  useEffect(() => {
    if (restoreStartedRef.current) {
      return;
    }
    restoreStartedRef.current = true;

    const stored = loadSession();
    if (!stored) {
      // No session: status was already initialised to 'unauthenticated' by the
      // lazy useState initialiser — nothing else to do here.
      return;
    }

    void (async () => {
      try {
        if (stored.type === 'jwt') {
          // Prime the client with the bearer token; server-side re-verification
          // is enforced on the next authenticated request (401 → unauthorized handler).
          const restoredUser = restoreUserFromJwt(stored.token);
          if (!mountedRef.current) {
            return;
          }
          if (restoredUser) {
            client?.setJwt(stored.token);
            setUser({
              ...(stored.user ?? {}),
              ...restoredUser,
              orgID: stored.user?.orgID || restoredUser.orgID || null,
            });
            setStatus('authenticated');
          } else {
            applySignedOut();
          }
          return;
        }

        const credentials: Credentials = { email: stored.email, password: stored.password };
        const restored = await authApi.restore(credentials);
        if (!mountedRef.current) {
          return;
        }
        if (restored) {
          credentialsRef.current = credentials;
          setUser(restored);
          setStatus('authenticated');
        } else {
          applySignedOut();
        }
      } catch {
        if (!mountedRef.current) {
          return;
        }
        // Could not verify (e.g. server unreachable): stay signed out but keep a
        // helpful message. The stored session is cleared to avoid ambiguity.
        setError('sessionExpired');
        applySignedOut();
      }
    })();
  }, [authApi, applySignedOut]);

  const login = useCallback(
    async (credentials: Credentials): Promise<boolean> => {
      setBusy(true);
      setError(null);
      try {
        const authenticatedUser = await authApi.login(credentials);
        credentialsRef.current = credentials;
        saveCredentials(credentials);
        setUser(authenticatedUser);
        setStatus('authenticated');
        return true;
      } catch (err) {
        setError(errorKeyOf(err));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [authApi],
  );

  const logout = useCallback(async (): Promise<void> => {
    const credentials = credentialsRef.current;
    setBusy(true);
    try {
      if (credentials) {
        await authApi.logout(credentials);
      }
    } finally {
      setBusy(false);
      setError(null);
      applySignedOut();
    }
  }, [authApi, applySignedOut]);

  const clearError = useCallback(() => setError(null), []);

  const signInWithJwt = useCallback(
    (token: string, signedInUser: PortalUser): void => {
      client?.setJwt(token);
      credentialsRef.current = null;
      saveSession({ type: 'jwt', token, user: signedInUser });
      setError(null);
      setUser(signedInUser);
      setStatus('authenticated');
    },
    [client],
  );

  const updateUser = useCallback((updatedUser: PortalUser): void => {
    setUser(updatedUser);
    const stored = loadSession();
    if (stored?.type === 'jwt') {
      saveSession({ ...stored, user: updatedUser });
    }
  }, []);

  const markOrganizationCreated = useCallback(() => setOrganizationJustCreated(true), []);

  const hasOrganization = Boolean(user?.orgID) || organizationJustCreated;

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      error,
      busy,
      hasOrganization,
      client,
      login,
      signInWithJwt,
      updateUser,
      logout,
      clearError,
      markOrganizationCreated,
    }),
    [
      status,
      user,
      error,
      busy,
      hasOrganization,
      client,
      login,
      signInWithJwt,
      updateUser,
      logout,
      clearError,
      markOrganizationCreated,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

interface ProviderApi {
  auth: AuthApi;
  client: PortalClient | null;
  register: ((handler: () => void) => void) | undefined;
}

function buildInjectedApi(auth: AuthApi): ProviderApi {
  return { auth, client: null, register: undefined };
}

function buildRealApi(): ProviderApi {
  const portal = createPortalApi();
  return {
    auth: portal.auth,
    client: portal.client,
    register: (handler) => portal.client.setUnauthorizedHandler(handler),
  };
}
